import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { MarkTestDialog, testMarkNote, unmarkTest } from "@/components/leads/MarkTestDialog";

const day = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const BTN = "border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white disabled:opacity-40";

function Row({ r, checked, onToggle }) {
  return (
    <label className="flex items-start gap-2 border-b border-zinc-200 px-2 py-1.5 text-xs cursor-pointer last:border-b-0" data-testid={`buyer-test-row-${r.id}`}>
      <input type="checkbox" checked={checked} onChange={onToggle} className="mt-0.5 accent-black" data-testid={`buyer-test-check-${r.id}`} />
      <span className="flex-1 min-w-0">
        <b>{r.name || "Lead"}</b> <span className="font-mono text-[10px] text-zinc-500">{r.state || "—"} · {fmtDate(r.delivered_at)} · {r.id.slice(0, 8)}{r.page ? ` · ${r.page}` : ""}</span>
        {r.is_test && (
          <span className="block text-[10px] text-amber-800" data-testid={`buyer-test-tag-${r.id}`}>
            <span className="bg-yellow-400 text-black px-1 mr-1 uppercase">Test</span>{testMarkNote(r.test_mark) || "Sent as a test lead (stays a test)"}
          </span>
        )}
      </span>
    </label>
  );
}

// Client page: find what this buyer got in a date range and mark the test sends so they stop counting.
export function BuyerTestLeads({ orgId }) {
  const [from, setFrom] = useState(day(7));
  const [to, setTo] = useState(day(0));
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState({});
  const [open, setOpen] = useState(false);
  const find = async () => {
    try {
      const { data } = await api.get(`/bulk/buyer/${orgId}/delivered-leads`, { params: { date_from: from, date_to: to } });
      setRows(data.leads);
      setSel({});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load their leads");
    }
  };
  const picked = (rows || []).filter((r) => sel[r.id]);
  const toMark = picked.filter((r) => !r.is_test).map((r) => r.id);
  const toUndo = picked.filter((r) => r.test_mark && !r.test_mark.sent_as_test).map((r) => r.id);
  const real = (rows || []).filter((r) => !r.is_test);
  const allReal = real.length > 0 && real.every((r) => sel[r.id]);
  const pickAllReal = () => setSel(allReal ? {} : Object.fromEntries(real.map((r) => [r.id, true])));
  return (
    <div className="space-y-3" data-testid="buyer-test-leads">
      <div className="text-[11px] text-zinc-500 leading-relaxed">
        Sent this buyer test leads? Find them here and mark them as test. They stop counting toward the buyer&apos;s spend, lead counts, caps and invoices.
      </div>
      <div className="flex flex-wrap items-end gap-2 text-xs">
        <label className="space-y-1"><span className="label-mono block">From</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="border border-black px-2 py-1" data-testid="buyer-test-from" /></label>
        <label className="space-y-1"><span className="label-mono block">To</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="border border-black px-2 py-1" data-testid="buyer-test-to" /></label>
        <button type="button" onClick={find} disabled={!from || !to} className={`${BTN} py-1.5`} data-testid="buyer-test-find">Find their leads</button>
      </div>
      {rows && (
        <div className="border border-black bg-white">
          <div className="flex flex-wrap items-center gap-2 border-b border-black px-2 py-1.5 text-[11px]">
            <label className="flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={allReal} onChange={pickAllReal} disabled={!real.length} className="accent-black" data-testid="buyer-test-select-all" />
              Select all not marked yet
            </label>
            <span className="label-mono ml-auto" data-testid="buyer-test-count">{rows.length} lead{rows.length === 1 ? "" : "s"}</span>
            <button type="button" disabled={!toMark.length} onClick={() => setOpen(true)} className={BTN} data-testid="buyer-test-mark">Mark {toMark.length || ""} as test</button>
            <button type="button" disabled={!toUndo.length} onClick={() => unmarkTest(toUndo, find)} className={BTN} data-testid="buyer-test-undo">Undo test{toUndo.length ? ` on ${toUndo.length}` : ""}</button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {rows.map((r) => <Row key={r.id} r={r} checked={!!sel[r.id]} onToggle={() => setSel({ ...sel, [r.id]: !sel[r.id] })} />)}
            {!rows.length && <div className="py-4 text-center label-mono" data-testid="buyer-test-empty">No leads delivered to them in these dates</div>}
          </div>
        </div>
      )}
      {open && <MarkTestDialog ids={toMark} onClose={() => setOpen(false)} onDone={find} />}
    </div>
  );
}
